import { readOrder } from './plays';
import type { Rng } from './rng';
import { buildRoute } from './routes';
import type { Tunables } from './tunables';
import type { Formation, Play, Player, RouteType, Shell } from './types';

export type SepLabel = 'open' | 'contested' | 'covered';

export interface Vec2 {
  x: number;
  y: number;
}

/** One eligible receiver on a live pass play. Coordinates are ball-relative: x right, y downfield, yards. */
export interface ReceiverPlan {
  slot: number;
  label: string;
  route: RouteType;
  points: Vec2[];
  /** Cumulative path length at each point. */
  lengths: number[];
  speed: number;
  breakTime: number | null;
  deep: boolean;
  /** Per-play noise, drawn once at the snap so the icon colour does not flicker. */
  noise: number;
  routeBonus: number;
  /** Defense slot index of the covering defender. */
  defender: number;
}

export interface PlaySetup {
  play: Play;
  defenseFormation: Formation;
  offense: Player[];
  defense: Player[];
  shell: Shell;
  flip: boolean;
  rng: Rng;
  t: Tunables;
  /** Seconds the pocket holds before the rush gets home (sack one second later). Infinity: no pressure. */
  pocket?: number;
  /** Line of scrimmage (0 to 100). With it, routes stay inside the field: they bend along the back of the end zone. */
  ballOn?: number;
}

/**
 * Keeps a route on the field. Past the back line margin it turns and runs a few yards along the back line
 * toward the middle, staying in its own half so receivers from both sides never cross; points beyond the
 * sideline margin come in to it.
 */
export function fitRouteToField(points: { x: number; y: number }[], ballOn: number, t: Tunables): { x: number; y: number }[] {
  const f = t.field;
  const maxY = 100 - ballOn + f.endZoneYards - f.routeBackLineMarginYards;
  const maxX = f.widthYards / 2 - f.routeSidelineMarginYards;
  const cx = (x: number) => Math.max(-maxX, Math.min(maxX, x));
  const out = [{ x: cx(points[0]!.x), y: Math.min(points[0]!.y, maxY) }];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!;
    const b = points[i]!;
    if (b.y <= maxY) {
      out.push({ x: cx(b.x), y: b.y });
      continue;
    }
    // Cut at the back line, then run along it toward the middle with what is left of the route.
    const k = a.y >= maxY ? 0 : (maxY - a.y) / (b.y - a.y);
    const hit = { x: cx(a.x + (b.x - a.x) * k), y: maxY };
    out.push(hit);
    let left = Math.hypot(b.x - hit.x, b.y - hit.y);
    for (let j = i + 1; j < points.length; j++) left += Math.hypot(points[j]!.x - points[j - 1]!.x, points[j]!.y - points[j - 1]!.y);
    const side = hit.x < 0 ? -1 : 1;
    const lane = Math.max(f.routeLaneMinYards, Math.abs(hit.x) - Math.min(left, f.routeBackLineRunYards));
    if (lane < Math.abs(hit.x)) out.push({ x: side * lane, y: maxY });
    break;
  }
  return out;
}

export function speedOf(p: Player, t: Tunables): number {
  return t.movement.baseSpeedYps + t.movement.perSpdYps * p.ratings.SPD;
}

export function sepLabel(sep: number, t: Tunables): SepLabel {
  if (sep >= t.separation.openAtOrAbove) return 'open';
  if (sep <= t.separation.coveredAtOrBelow) return 'covered';
  return 'contested';
}

/** routeVsCoverageBonus (GDD section 7): exact shell first, then "zone" for any zone shell. */
export function routeBonus(route: RouteType, shell: Shell, wr: Player, def: Player, t: Tunables): number {
  const table = t.separation.routeVsCoverage as Record<string, Record<string, number> | undefined>;
  const row = table[route];
  if (!row) return 0;
  const bonus = row[shell] ?? (shell !== 'man' ? row['zone'] : undefined) ?? 0;
  if (bonus && t.separation.requiresFasterReceiver.includes(route) && wr.ratings.SPD <= def.ratings.SPD) return 0;
  return bonus;
}

/** phaseBonus (GDD section 7): +10 just after the break, -8 before the break on deep routes. */
export function phaseBonus(r: Pick<ReceiverPlan, 'breakTime' | 'deep'>, time: number, t: Tunables): number {
  const s = t.separation;
  if (r.breakTime === null) return 0;
  if (time >= r.breakTime && time <= r.breakTime + s.breakBonusSeconds) return s.breakBonus;
  if (time < r.breakTime && r.deep) return s.deepPreBreakPenalty;
  return 0;
}

/** sep = (WR.RTE + WR.SPD)/2 - (DEF.COV + DEF.SPD)/2 + routeVsCoverageBonus + phaseBonus + noise */
export function separationFormula(wr: Player, def: Player, routeBonusValue: number, phase: number, noise: number): number {
  return (wr.ratings.RTE + wr.ratings.SPD) / 2 - (def.ratings.COV + def.ratings.SPD) / 2 + routeBonusValue + phase + noise;
}

function lerp(a: number, b: number, k: number): number {
  return a + (b - a) * k;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/**
 * The live model of one pass play. Everything is a pure function of time after the snap,
 * so the same seed and the same throw time always give the same picture and the same numbers.
 */
export class PlaySim {
  readonly receivers: ReceiverPlan[];
  readonly offenseStart: Vec2[];
  readonly defenseStart: Vec2[];
  /** Where each defender settles when not tracking a receiver. */
  private readonly defenseHome: Vec2[];
  private readonly rushers: Set<number>;
  private readonly isZone: boolean;
  /** Lookups so positionsAt() does no searching or allocating per frame. */
  private readonly receiverOfSlot: number[];
  private readonly receiverOfDefender: number[];
  readonly qbSlot: number;

  constructor(private readonly s: PlaySetup) {
    const { play, defenseFormation, offense, defense, shell, flip, rng, t } = s;
    const mirror = flip ? -1 : 1;
    this.isZone = shell !== 'man';
    this.offenseStart = play.formation.slots.map((sl) => ({ x: sl.x * mirror, y: sl.y }));
    this.defenseStart = defenseFormation.slots.map((sl) => ({ x: sl.x * mirror, y: sl.y }));
    this.rushers = new Set(defenseFormation.slots.flatMap((sl, i) => (sl.pos === 'DL' ? [i] : [])));
    this.defenseHome = this.defenseStart.map((p) => ({ ...p }));

    const order = readOrder(play);
    const labels = 'ABCDE';
    const plans = order.map((slot, k) => {
      const pr = play.routes.find((r) => r.slot === slot);
      const startSlot = play.formation.slots[slot];
      const wr = offense[slot];
      if (!pr || !startSlot || !wr) throw new Error(`Bad route for slot ${slot}`);
      const built = buildRoute(pr.route, startSlot, flip, pr.depth);
      if (this.s.ballOn !== undefined) {
        built.points = fitRouteToField(built.points, this.s.ballOn, t);
        built.breakIndex = Math.min(built.breakIndex, built.points.length - 1);
      }
      const lengths = [0];
      for (let i = 1; i < built.points.length; i++) {
        const a = built.points[i - 1]!;
        const b = built.points[i]!;
        lengths.push(lengths[i - 1]! + Math.hypot(b.x - a.x, b.y - a.y));
      }
      const speed = speedOf(wr, t);
      const breakTime = built.breakIndex > 0 ? t.movement.releaseSeconds + lengths[built.breakIndex]! / speed : null;
      return {
        slot,
        label: labels[k] ?? '?',
        route: pr.route,
        points: built.points,
        lengths,
        speed,
        breakTime,
        deep: t.separation.deepRoutes.includes(pr.route),
        noise: rng.range(t.separation.noiseMin, t.separation.noiseMax),
        routeBonus: 0,
        defender: -1,
      } satisfies ReceiverPlan;
    });

    this.assignCoverage(plans, shell, t);
    for (const r of plans) {
      const wr = offense[r.slot]!;
      const def = defense[r.defender]!;
      r.routeBonus = routeBonus(r.route, shell, wr, def, t);
    }
    this.receivers = plans;
    this.receiverOfSlot = play.formation.slots.map((_, i) => plans.findIndex((r) => r.slot === i));
    this.receiverOfDefender = defenseFormation.slots.map((_, j) => plans.findIndex((r) => r.defender === j));
    this.qbSlot = play.formation.slots.findIndex((sl) => sl.pos === 'QB');
  }

  /** Man: outside receivers draw corners, the rest safeties then backers. Zone: defenders take landmarks; a receiver belongs to the nearest one where his route ends. */
  private assignCoverage(plans: ReceiverPlan[], shell: Shell, t: Tunables): void {
    const { defenseFormation } = this.s;
    const free = defenseFormation.slots.map((_, i) => i).filter((i) => !this.rushers.has(i));
    const landmarks = (t.coverage.landmarks[shell] as number[][]).map(([x = 0, y = 0]) => ({ x: x * (this.s.flip ? -1 : 1), y }));

    if (shell === 'man') {
      const prefer = ['CB', 'S', 'LB'];
      const taken = new Set<number>();
      const byWidth = [...plans].sort((a, b) => Math.abs(this.offenseStart[b.slot]!.x) - Math.abs(this.offenseStart[a.slot]!.x));
      for (const r of byWidth) {
        const rx = this.offenseStart[r.slot]!.x;
        let best = -1;
        for (const pos of prefer) {
          const cands = free.filter((i) => !taken.has(i) && defenseFormation.slots[i]!.pos === pos);
          if (cands.length === 0) continue;
          best = cands.reduce((a, b) => (Math.abs(this.defenseStart[a]!.x - rx) <= Math.abs(this.defenseStart[b]!.x - rx) ? a : b));
          break;
        }
        if (best < 0) best = free.find((i) => !taken.has(i)) ?? free[0] ?? 0;
        taken.add(best);
        r.defender = best;
      }
      // Anyone left over plays deep middle (the landmark) or holds.
      const spare = free.filter((i) => !taken.has(i));
      spare.forEach((i, k) => {
        const lm = landmarks[k];
        if (lm) this.defenseHome[i] = lm;
      });
      return;
    }

    // Zone: greedy nearest landmark per defender.
    const used = new Set<number>();
    for (const i of free) {
      const start = this.defenseStart[i]!;
      let bestK = -1;
      let bestD = Infinity;
      landmarks.forEach((lm, k) => {
        if (used.has(k)) return;
        const d = Math.hypot(lm.x - start.x, lm.y - start.y);
        if (d < bestD) {
          bestD = d;
          bestK = k;
        }
      });
      if (bestK >= 0) {
        used.add(bestK);
        this.defenseHome[i] = landmarks[bestK]!;
      }
    }
    for (const r of plans) {
      const end = r.points[r.points.length - 1]!;
      r.defender = free.reduce((a, b) => {
        const ha = this.defenseHome[a]!;
        const hb = this.defenseHome[b]!;
        return Math.hypot(ha.x - end.x, ha.y - end.y) <= Math.hypot(hb.x - end.x, hb.y - end.y) ? a : b;
      });
    }
  }

  /** Receiver position along his route at a time after the snap. */
  receiverPos(index: number, time: number, out: Vec2): Vec2 {
    const r = this.receivers[index]!;
    const dist = Math.max(0, time - this.s.t.movement.releaseSeconds) * r.speed;
    const last = r.points.length - 1;
    if (dist >= r.lengths[last]!) {
      out.x = r.points[last]!.x;
      out.y = r.points[last]!.y;
      return out;
    }
    let i = 1;
    while (i < last && r.lengths[i]! < dist) i++;
    const a = r.points[i - 1]!;
    const b = r.points[i]!;
    const seg = r.lengths[i]! - r.lengths[i - 1]!;
    const k = seg > 0 ? (dist - r.lengths[i - 1]!) / seg : 1;
    out.x = lerp(a.x, b.x, k);
    out.y = lerp(a.y, b.y, k);
    return out;
  }

  separation(index: number, time: number): number {
    const r = this.receivers[index]!;
    const wr = this.s.offense[r.slot]!;
    const def = this.s.defense[r.defender]!;
    return separationFormula(wr, def, r.routeBonus, phaseBonus(r, time, this.s.t), r.noise);
  }

  /** Index into receivers for an offense slot, or -1. */
  receiverIndexOfSlot(slot: number): number {
    return this.receiverOfSlot[slot] ?? -1;
  }

  private readonly tmp: Vec2 = { x: 0, y: 0 };

  /** Writes every player's ball-relative position at a time after the snap. Arrays are [x0, y0, x1, y1, ...]. */
  positionsAt(time: number, offenseOut: Float32Array, defenseOut: Float32Array): void {
    const t = this.s.t;
    const m = t.movement;
    const slots = this.s.play.formation.slots;
    for (let i = 0; i < slots.length; i++) {
      const start = this.offenseStart[i]!;
      let x = start.x;
      let y = start.y;
      const ri = this.receiverIndexOfSlot(i);
      if (ri >= 0) {
        this.receiverPos(ri, time, this.tmp);
        x = this.tmp.x;
        y = this.tmp.y;
      } else if (slots[i]!.pos === 'QB') {
        y -= m.qbDropYards * clamp(time / m.qbDropSeconds, 0, 1);
      } else if (slots[i]!.pos === 'OL') {
        y -= m.olGiveYards * clamp(time / m.dlPushSeconds, 0, 1);
        const pocket = this.s.pocket ?? Infinity;
        if (time > pocket) y -= t.pocket.closeYards * clamp((time - pocket) / t.pocket.sackAfterSeconds, 0, 1) * 2;
      }
      offenseOut[i * 2] = x;
      offenseOut[i * 2 + 1] = y;
    }

    const c = t.coverage;
    const settle = clamp(time / c.settleSeconds, 0, 1);
    for (let j = 0; j < this.defenseStart.length; j++) {
      const start = this.defenseStart[j]!;
      let x = start.x;
      let y = start.y;
      if (this.rushers.has(j)) {
        const push = m.dlPushYards * clamp(time / m.dlPushSeconds, 0, 1);
        y -= push;
        // Pocket breaks: the rush closes on the QB and arrives at the sack time.
        const pocket = this.s.pocket ?? Infinity;
        if (time > pocket) {
          const k = clamp((time - pocket) / t.pocket.sackAfterSeconds, 0, 1);
          const qbX = this.offenseStart[this.qbSlot]?.x ?? 0;
          const qbY = (this.offenseStart[this.qbSlot]?.y ?? 0) - m.qbDropYards;
          const spread = (start.x - qbX) * 0.15;
          x = lerp(x, qbX + spread, k);
          y = lerp(y, qbY + t.pocket.closeYards, k);
        }
      } else {
        const home = this.defenseHome[j]!;
        x = lerp(start.x, home.x, settle);
        y = lerp(start.y, home.y, settle);
        const ri = this.receiverOfDefender[j] ?? -1;
        if (ri >= 0) {
          const recv = this.receiverPos(ri, time, this.tmp);
          const sep = this.separation(ri, time);
          const trail = clamp(c.trailBaseYards + c.trailPerSepYards * sep, c.trailMinYards, c.trailMaxYards);
          // Trail behind and slightly inside the receiver, so open receivers visibly have space downfield.
          const inside = recv.x > 0 ? -1 : 1;
          const tx = recv.x + inside * trail * 0.35;
          const ty = recv.y - trail;
          let w = settle;
          if (this.isZone) {
            const d = Math.hypot(recv.x - home.x, recv.y - home.y);
            w = clamp(1 - d / c.zoneRadiusYards, 0, 1) * settle;
          }
          x = lerp(x, tx, w);
          y = lerp(y, ty, w);
        }
      }
      defenseOut[j * 2] = x;
      defenseOut[j * 2 + 1] = y;
    }
  }
}
